<template lang="pug">
.needs-meals-table-component
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    //- フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    //- ヘッダートップ
    //- ライブラリに列を描画させると列結合等レイアウト&デザインが意図通りに出来ないので自作の列を作成する
    //- ライブラリが描画する列はスタイルで非表示にしている
    template(#thead-top="data")
      b-tr.first-line
        b-th.date(rowspan="2")
          span 日
        b-th.needs-meals(:colspan="_mealCancellationColspan")
          span 食事キャンセル件数
        b-th.list(rowspan="2")
          span 一覧
      b-tr.second-line
        b-th.meal(v-if="displayMealRow('breakfast')")
          span 朝食
        b-th.meal(v-if="displayMealRow('lunch')")
          span 昼食
        b-th.meal(v-if="displayMealRow('snack')")
          span おやつ
        b-th.meal(v-if="displayMealRow('dinner')")
          span 夕食

    //- セル：日
    template(#cell(date)="row")
      .d-flex.flex-row
        span {{ getDate(row.item.datetime) }}
        span （
        span(:class="weekCssClass(row.item.datetime)") {{ getWeek(row.item.datetime) }}
        span ）
    //- セル：食事キャンセル・朝食
    template(#cell(breakfast)="row")
      span.mr-3 {{ getMealCount(row.item, 'breakfast') }}
      button.btn.detail-btn(@click="onDetail(row.item, 'breakfast')"
        :class="{ 'disable': disableDetailBtn(row.item, 'breakfast') }" :disabled="disableDetailBtn(row.item, 'breakfast')") 詳細
    //- セル：食事キャンセル・昼食
    template(#cell(lunch)="row")
      span.mr-3 {{ getMealCount(row.item, 'lunch') }}
      button.btn.detail-btn(@click="onDetail(row.item, 'lunch')"
        :class="{ 'disable': disableDetailBtn(row.item, 'lunch') }" :disabled="disableDetailBtn(row.item, 'lunch')") 詳細
    //- セル：食事キャンセル・おやつ
    template(#cell(snack)="row")
      span.mr-3 {{ getMealCount(row.item, 'snack') }}
      button.btn.detail-btn(@click="onDetail(row.item, 'snack')"
        :class="{ 'disable': disableDetailBtn(row.item, 'snack') }" :disabled="disableDetailBtn(row.item, 'snack')") 詳細
    //- セル：食事キャンセル・夕食
    template(#cell(dinner)="row")
      span.mr-3 {{ getMealCount(row.item, 'dinner') }}
      button.btn.detail-btn(@click="onDetail(row.item, 'dinner')"
        :class="{ 'disable': disableDetailBtn(row.item, 'dinner') }" :disabled="disableDetailBtn(row.item, 'dinner')") 詳細
    //- セル：一覧
    template(#cell(list)="row")
      button.btn.detail-btn(@click="onListDetail(row.item)") 詳細
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { ExtNeedsMeal, OutingNeedsMealsGetModel } from "@/commons/services/Outings";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import { OutingSettingGetModel } from "@/commons/services/OutingSettings";

/**
 * ### 外出外泊届けテーブルコンポーネント
 */
@Component({
  components: {
    HelpIcon
  }
})
export default class NeedsMealsTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<NeedsMealsTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<NeedsMealsTableField>;
  /** 全体行数 */
  @Prop()
  totalRows!: number;
  /** 現在のページ */
  @Prop()
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop()
  perPage!: number;
  /** ローディング表示可否 */
  @Prop()
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: any;
  /** 外出外泊届け設定 */
  @Prop()
  setting!: OutingSettingGetModel;

  get _value(): Array<NeedsMealsTableItem> {
    return this.value;
  }

  get _mealCancellationColspan(): number {
    if (!this.setting) {
      return 0;
    }
    return Object.values(this.setting.useNeedsMeal).filter((value) => value === true).length;
  }

  displayMealRow(mealType: MealType): boolean {
    if (!this.setting) {
      return false;
    }

    if (!this.setting.useNeedsMeal[mealType]) {
      return false;
    }
    return this.setting.useNeedsMeal[mealType];
  }

  /**
   * ### 時分を返す
   */
  public getTime(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "hh:MM");
  }

  /**
   * ### 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "mm月dd日");
  }

  /**
   * ### 曜日を返す
   */
  public getWeek(date: number): string {
    const dateObject: Date = this.$dateUtil.convertNumberToDate(date);
    return this.$dateUtil.getWeekName(dateObject, "ja");
  }

  /**
   * ### 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(date: number): string {
    let weekClassName: string = "";
    const weekName: string = this.getWeek(date);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * ### 食事キャンセルの件数を返す
   *
   * - 引数で渡された食事種別のキャンセル数をカウントして返す
   */
  public getMealCount(item: NeedsMealsTableItem, mealType: MealType): string {
    let count: number = 0;
    for (const needsMeal of item.needsMeals) {
      if (!needsMeal[mealType] && needsMeal.countTarget) {
        count++;
      }
    }
    return `${count}件`;
  }

  /**
   * ### 食事キャンセルの詳細ボタンの非活性かどうか
   */
  public disableDetailBtn(item: NeedsMealsTableItem, mealType: MealType): boolean {
    for (const needsMeal of item.needsMeals) {
      if (!needsMeal[mealType]) {
        return false;
      }
    }
    return true;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(item: NeedsMealsTableItem, filter: FilterSetting): boolean {
    return this.filterGroupId(item, filter);
  }

  public filterGroupId(item: NeedsMealsTableItem, filter: FilterSetting): boolean {
    if (filter.id == "") {
      return true;
    }

    for (const needsMeal of item.needsMeals) {
      if (needsMeal.residentGroupId == Number(filter.id)) {
        return true;
      }
    }
    return false;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    // 各行の表示情報を更新するため、親コンポーネントの表示対象更新を呼び出す
    this.$emit("updateCountTargetByGroupId");
    return;
  }

  /**
   * ### 食事キャンセル詳細ボタン押下イベント処理
   */
  public onDetail(item: NeedsMealsTableItem, mealType: MealType): void {
    this.$emit("onDetail", { item, mealType });
    return;
  }

  /**
   * ### 一覧詳細ボタン押下イベント処理
   */
  public onListDetail(item: NeedsMealsTableItem): void {
    this.$emit("onListDetail", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface NeedsMealsTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface NeedsMealsTableItem extends OutingNeedsMealsGetModel {
  /** 食事キャンセル情報 */
  needsMeals: Array<TableItemNeedsMeal>;
}

export interface TableItemNeedsMeal extends ExtNeedsMeal {
  /** カウント対象かどうか */
  countTarget: boolean;
}

export enum MealType {
  /** 朝食 */
  BREAKFAST = "breakfast",
  /** 昼食 */
  LUNCH = "lunch",
  /** おやつ */
  SNACK = "snack",
  /** 夕食 */
  DINNER = "dinner"
}

export interface FilterSetting {
  id: string;
}
</script>

<style lang="scss" scoped>
.outing-table-component {
  .detail-btn,
  .consented-btn,
  .refuse-btn {
    min-width: 57px;
  }
  .cancel-btn {
    min-width: 120px;
  }
}
</style>
